import { useMutation } from '@apollo/client';
import { Button, TextArea, TextInput } from '@ibinder/common-frontend';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { IMPERSONATE } from '../../../../../../api/queries/queries_local';

const Container = styled.div`
  width: 450px;
  display: flex;
  flex-direction: column;
  min-height: 350px;
  background: ${({ theme }) => theme.colors.accent};
  box-shadow: ${({ theme }) => theme.shadows.primaryWithOpacity};
  padding: 1rem;
  position: fixed;
  top: 50%;
  left: 40%;
  transform: translate(-50%, -50%);

  button {
    margin-top: auto;
  }
`;

const FieldContainer = styled.div`
  margin-bottom: 1rem;
`;

const ErrorText = styled.div`
  color: red;
  margin-bottom: 1rem;
`;

type Props = {
  targetUserId: string;
  userName: string;
  onClose: () => void;
  onImpersonated: (token: string) => void;
};

const ImpersonateUserModal = ({
  targetUserId,
  userName,
  onClose,
  onImpersonated
}: Props) => {
  const modalNode = useRef<HTMLDivElement>();
  const [reason, setReason] = useState('');
  const [supportCase, setSupportCase] = useState('');
  const [showRequiredError, setShowRequiredError] = useState(false);

  const [runImpersonate] = useMutation(IMPERSONATE, {
    onCompleted: (data) => onImpersonated(data.post.impersonationToken)
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!modalNode.current?.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const getInputValue = (value) => value?.target?.value ?? value;

  const handleClick = () => {
    if (!reason.trim()) {
      setShowRequiredError(true);
      return;
    }

    runImpersonate({
      variables: {
        input: {
          targetUserId,
          reason: reason.trim(),
          supportCase: supportCase.trim() || undefined
        }
      }
    });
  };

  return (
    <Container ref={modalNode}>
      <h2>{`Impersonate user ${userName}?`}</h2>
      <FieldContainer>
        <TextArea
          label="Reason"
          value={reason}
          changeValue={(value) => {
            setReason(getInputValue(value));
            setShowRequiredError(false);
          }}
          required={true}
        />
      </FieldContainer>
      <FieldContainer>
        <TextInput
          label="Support case"
          value={supportCase}
          changeValue={(value) => setSupportCase(getInputValue(value))}
        />
      </FieldContainer>
      {showRequiredError && <ErrorText>Reason is required</ErrorText>}
      <Button btnStyle="primary" label="Continue?" onClick={handleClick} />
    </Container>
  );
};

export default ImpersonateUserModal;
